import { Chain } from '@ankr.com/chains-list';
import { useMemo } from 'react';

import { chainsRoutesConfig } from 'domains/chains/routes';
import { selectPublicChainById } from 'modules/chains/store/selectors';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAddNetwork } from 'modules/wallet/hooks/useAddNetwork';
import { getNetworkParams } from 'modules/wallet/utils/getNetworkParams';
import { useAppSelector } from 'store/useAppSelector';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useChainSelectors } from 'modules/chains/components/ChainSelectorsProvider';
import { useSignInWithRedirectURL } from 'modules/auth/hooks/useSignInWithRedirectURL';

interface UseAddNetworkHandlerProps {
  chain: Chain;
}

export const useAddNetworkHandler = ({ chain }: UseAddNetworkHandlerProps) => {
  const { isLoggedIn } = useAuth();
  const { isEnterprise } = useAccountStatus();
  const { handleAddNetwork } = useAddNetwork();

  const publicChain = useAppSelector(state =>
    selectPublicChainById(state, chain.id),
  );

  const { chainSubType, chainType, group } = useChainSelectors();

  const { networkConfiguration } = useMemo(
    () =>
      getNetworkParams({
        chain: isLoggedIn ? chain : publicChain || chain,
        chainSubType,
        chainType,
        group,
        isEnterprise,
      }),
    [
      chain,
      publicChain,
      chainType,
      chainSubType,
      group,
      isLoggedIn,
      isEnterprise,
    ],
  );

  const signIn = useSignInWithRedirectURL({
    redirectURL: chainsRoutesConfig.chainDetails.generatePath(chain.id),
    hasAPIKeysNotification: true,
  });

  if (!networkConfiguration) {
    return undefined;
  }

  if (!isLoggedIn && group?.chains.every(_chain => !_chain.isPublic)) {
    return signIn;
  }

  return () => handleAddNetwork(networkConfiguration);
};
